import * as React from 'react'
import { isTabularServiceFilledColumn, tabularEditCountsText } from '@atbash-labs/ontology-core'
import {
  useGetTabularIngestJobPageQuery,
  type TabularIngestJobPage,
  type TabularIngestJobPageSummary,
  type TabularIngestJobPageTable,
  type TabularPageEditIssue,
  type TabularRunPageReading
} from '@axilon-apps/domains-ontology-ui-state'
import { Button } from '../../components/ui/button'
import { Card } from '../../components/ui/card'
import { formatRelativeTime } from '../../lib/time'
import { cn } from '../../lib/utils'
import {
  PageImageViewer,
  PageStatusChip,
  pageNeedsAttention,
  percent
} from '../IngestPage/shared/PageImageViewer'
import { WriteButton } from '../IngestPage/shared/WriteButton'
import {
  COMPACT_ACTION,
  DESTRUCTIVE_ACTION,
  DESTRUCTIVE_OUTLINE
} from '../IngestPage/shared/compact'
import type { TabularRowRef } from './route'
import {
  addDraftRow,
  removeAddedRow,
  savedPageDraft,
  setAddedCell,
  setStoredCell,
  toggleDeletedRow,
  type RunPageEdits,
  type TableEditDraft
} from './useRunPageEdits'
import type { RunPageReview, RunReviewDecision, RunReviewDecisions } from './useRunPageReview'

export type RunPage = TabularIngestJobPageSummary
/** `done`: flagged pages someone has reviewed or excluded. Offered only on a run held for review. */
export type PageFilter = 'todo' | 'done' | 'all'

/** Fields the model read with less confidence than this are listed as the reason a row was flagged. */
export const LOW_CONFIDENCE = 0.8

export function needsAttention(page: RunPage): boolean {
  return pageNeedsAttention(page.status)
}

/** A flagged page no one has reviewed or excluded yet. */
export function awaitingDecision(page: RunPage, decisions?: RunReviewDecisions): boolean {
  return needsAttention(page) && decisions?.[page.pageId] === undefined
}

export function pagesInFilter(
  pages: readonly RunPage[],
  filter: PageFilter,
  decisions?: RunReviewDecisions
): RunPage[] {
  if (filter === 'todo') return pages.filter((page) => awaitingDecision(page, decisions))
  if (filter === 'done') return pages.filter((page) => decisions?.[page.pageId] !== undefined)
  return [...pages]
}

/** The run opens on its first page still to review, or its first page when none is. */
export function defaultFilter(
  pages: readonly RunPage[],
  decisions?: RunReviewDecisions
): PageFilter {
  return pages.some((page) => awaitingDecision(page, decisions)) ? 'todo' : 'all'
}

/** The next flagged page after `pageId` that still needs a decision, wrapping round to the start. */
export function nextAwaitingDecision(
  pages: readonly RunPage[],
  pageId: string,
  decisions?: RunReviewDecisions
): RunPage | undefined {
  const at = pages.findIndex((page) => page.pageId === pageId)
  return [...pages.slice(at + 1), ...pages.slice(0, Math.max(at, 0))].find((page) =>
    awaitingDecision(page, decisions)
  )
}

/**
 * J / K step to the next / previous page in `order`, unless the reader is
 * typing somewhere or holding a modifier.
 */
export function usePageSteps(
  order: readonly RunPage[],
  currentId: string | undefined,
  go: (pageId: string) => void
) {
  React.useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.metaKey || event.ctrlKey || event.altKey) return
      const key = event.key.toLowerCase()
      if (key !== 'j' && key !== 'k') return
      const target = event.target instanceof Element ? event.target : null
      if (target?.closest('input, textarea, select, [contenteditable="true"]')) return
      const at = order.findIndex((page) => page.pageId === currentId)
      const next = key === 'j' ? (at < 0 ? 0 : at + 1) : at - 1
      const page = order[next]
      if (!page) return
      event.preventDefault()
      go(page.pageId)
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [order, currentId, go])
}

/**
 * The run's Pages tab: the page list on the left, the chosen page on the
 * right with its image, why it was flagged, and the rows it produced.
 */
export function PagesWorkspace({
  jobId,
  pages,
  selectedPageId,
  highlight,
  review,
  onSelect,
  openAsPage
}: {
  jobId: string
  pages: readonly RunPage[]
  selectedPageId?: string
  highlight?: TabularRowRef
  /** Present on a run that was held for review. */
  review?: RunPageReview
  onSelect: (pageId: string) => void
  openAsPage: (pageId: string, filter: PageFilter) => React.ReactNode
}) {
  const decisions = review?.decisions
  const [filter, setFilter] = React.useState<PageFilter>(() => defaultFilter(pages, decisions))
  const visible = React.useMemo(
    () => pagesInFilter(pages, filter, decisions),
    [pages, filter, decisions]
  )
  const current = pages.find((page) => page.pageId === selectedPageId) ?? visible[0] ?? pages[0]
  const select = useLeaveGuard(review, current?.pageId, onSelect)
  const hiddenByFilter = current !== undefined && !visible.includes(current)
  // Only a newly shown page outside the filter widens it. Deciding the last
  // page to review leaves that page on screen and the To review list empty.
  const shownPageId = React.useRef<string | undefined>(undefined)
  React.useEffect(() => {
    if (current?.pageId === shownPageId.current) return
    shownPageId.current = current?.pageId
    if (hiddenByFilter) setFilter('all')
  }, [current?.pageId, hiddenByFilter])
  usePageSteps(visible, current?.pageId, select)
  if (!current) return null
  return (
    <div className="grid min-w-0 gap-3 lg:grid-cols-[14rem_minmax(0,1fr)]">
      <PageList
        pages={pages}
        visible={visible}
        filter={filter}
        onFilter={setFilter}
        decisions={decisions}
        edited={review?.edited}
        selectedPageId={current.pageId}
        onSelect={select}
      />
      <PagePane
        jobId={jobId}
        page={current}
        pages={pages}
        highlight={highlight}
        review={review}
        onSelect={select}
        action={openAsPage(current.pageId, filter)}
      />
    </div>
  )
}

/** `go`, asking first when the page being edited has unsaved edits. */
export function useLeaveGuard(
  review: RunPageReview | undefined,
  currentPageId: string | undefined,
  go: (pageId: string) => void
): (pageId: string) => void {
  const confirmLeave = review?.edits.confirmLeave
  return React.useCallback(
    (pageId: string) => {
      if (pageId !== currentPageId && confirmLeave && !confirmLeave()) return
      go(pageId)
    },
    [confirmLeave, currentPageId, go]
  )
}

function PageList({
  pages,
  visible,
  filter,
  onFilter,
  decisions,
  edited,
  selectedPageId,
  onSelect
}: {
  pages: readonly RunPage[]
  visible: readonly RunPage[]
  filter: PageFilter
  onFilter: (filter: PageFilter) => void
  decisions?: RunReviewDecisions
  edited?: RunPageReview['edited']
  selectedPageId: string
  onSelect: (pageId: string) => void
}) {
  const toReview = pages.filter((page) => awaitingDecision(page, decisions)).length
  const decided = pages.filter((page) => decisions?.[page.pageId] !== undefined).length
  const files = [...new Set(visible.map((page) => page.fileName))]
  return (
    <Card className="h-fit min-w-0 gap-2 p-2" aria-label="Run pages">
      <div className="flex flex-wrap gap-1" role="group" aria-label="Page filter">
        <FilterPill active={filter === 'todo'} onClick={() => onFilter('todo')}>
          To review {toReview}
        </FilterPill>
        {decisions ? (
          <FilterPill active={filter === 'done'} onClick={() => onFilter('done')}>
            Done {decided}
          </FilterPill>
        ) : null}
        <FilterPill active={filter === 'all'} onClick={() => onFilter('all')}>
          All {pages.length}
        </FilterPill>
      </div>
      <div className="flex max-h-[70vh] flex-col gap-2 overflow-y-auto">
        {files.map((file) => (
          <div key={file} className="flex flex-col gap-0.5">
            <p className="truncate px-2 text-10 text-ink-3" title={file}>
              {file}
            </p>
            {visible
              .filter((page) => page.fileName === file)
              .map((page) => (
                <button
                  key={page.pageId}
                  type="button"
                  aria-current={page.pageId === selectedPageId ? 'page' : undefined}
                  className={cn(
                    'flex w-full items-center justify-between gap-2 rounded px-2 py-1 text-left text-11',
                    page.pageId === selectedPageId ? 'bg-surface-2 text-ink' : 'hover:bg-surface-1'
                  )}
                  onClick={() => onSelect(page.pageId)}
                >
                  <span className="min-w-0 truncate">
                    Page {page.pageNumber}
                    {page.confidence !== undefined ? (
                      <span className="ml-1.5 text-10 text-ink-3 tabular-nums">
                        {percent(page.confidence)}
                      </span>
                    ) : null}
                    {edited?.[page.pageId] ? (
                      <span
                        className="ml-1.5 text-10 text-st-info"
                        title={`Rows edited: ${tabularEditCountsText(edited[page.pageId])}`}
                      >
                        edited
                      </span>
                    ) : null}
                  </span>
                  <DecisionChip
                    decision={decisions?.[page.pageId]?.decision}
                    status={page.status}
                  />
                </button>
              ))}
          </div>
        ))}
        {visible.length === 0 ? (
          <p className="px-2 text-11 text-ink-3">
            {filter === 'done'
              ? 'No page is reviewed or excluded yet.'
              : decisions && pages.some(needsAttention)
                ? 'Every flagged page is reviewed or excluded.'
                : 'No page was flagged.'}
          </p>
        ) : null}
      </div>
    </Card>
  )
}

function FilterPill({
  active,
  onClick,
  children
}: {
  active: boolean
  onClick: () => void
  children: React.ReactNode
}) {
  return (
    <button
      type="button"
      aria-pressed={active}
      onClick={onClick}
      className={cn(
        'rounded-full border px-2 py-0.5 text-11 tabular-nums',
        active ? 'border-ink bg-surface-2 text-ink' : 'border-line text-ink-3 hover:text-ink'
      )}
    >
      {children}
    </button>
  )
}

/** A decided page reads as its decision; any other page as what the run made of it. */
function DecisionChip({
  decision,
  status
}: {
  decision?: RunReviewDecision
  status: RunPage['status']
}) {
  if (!decision) return <PageStatusChip status={status} />
  return (
    <span
      className={cn(
        'rounded border px-1 py-0.5 text-10 uppercase whitespace-nowrap',
        decision === 'reviewed' ? 'border-st-info/50 text-st-info' : 'border-line text-ink-3'
      )}
    >
      {decision}
    </span>
  )
}

/** One page of a run: what the vision call saw, why it was flagged, and the rows it produced. */
export function PagePane({
  jobId,
  page,
  pages,
  highlight,
  review,
  onSelect,
  action
}: {
  jobId: string
  page: RunPage
  pages: readonly RunPage[]
  highlight?: TabularRowRef
  review?: RunPageReview
  /** Shows another page; the review bar moves on to the next flagged page through it. */
  onSelect?: (pageId: string) => void
  action?: React.ReactNode
}) {
  const query = useGetTabularIngestJobPageQuery({ jobId, pageId: page.pageId })
  const detail = query.currentData
  const decision = review?.decisions[page.pageId]?.decision
  const edits = review?.edits
  const draft = edits?.draft?.pageId === page.pageId ? edits.draft : undefined
  return (
    <Card className="min-w-0 gap-3 p-3" data-testid={`run-page-${page.pageId}`}>
      <PageHeader
        page={page}
        pages={pages}
        review={review}
        detail={detail}
        onSelect={onSelect}
        action={action}
      />
      <div className="grid min-w-0 gap-3 xl:grid-cols-[minmax(0,0.85fr)_minmax(0,1.3fr)]">
        <PageImageViewer
          key={page.pageId}
          imageUrl={page.status === 'blank' ? undefined : page.imageUrl}
          preprocessedImageUrl={page.preprocessedImageUrl}
          fileName={page.fileName}
          pageNumber={page.pageNumber}
          missingText={page.status === 'blank' ? undefined : 'The run kept no image of this page.'}
        />
        <div className="flex min-w-0 flex-col gap-4">
          <PageReason page={page} reading={detail?.reading} />
          <ExtractionNotes page={page} />
          {query.isError ? (
            <p role="alert" className="text-xs text-st-down">
              This page’s rows could not be loaded.
            </p>
          ) : !detail ? (
            <p role="status" className="text-xs text-ink-3">
              Loading this page’s rows…
            </p>
          ) : detail.tables.length === 0 ? (
            <p className="text-xs text-ink-3">The run kept no output tables.</p>
          ) : (
            detail.tables.map((table) => (
              <PageTableRows
                key={table.table}
                table={table}
                reading={detail.reading?.tables.find(
                  (candidate) => candidate.table === table.table
                )}
                highlight={highlight?.table === table.table ? highlight.position : undefined}
                decision={decision}
                held={review?.open === true}
                edit={
                  draft && edits
                    ? {
                        draft: draft.tables[table.table],
                        issues: edits.issues.filter((issue) => issue.table === table.table),
                        update: edits.update,
                        disabled: edits.saving
                      }
                    : undefined
                }
              />
            ))
          )}
        </div>
      </div>
    </Card>
  )
}

/**
 * The page's name and status, with what a reviewer may do with it on a run
 * held for review: mark a flagged page reviewed or exclude it, and edit the
 * rows of any page. Once the run has committed or been discarded, it only
 * says what was decided and edited.
 */
function PageHeader({
  page,
  pages,
  review,
  detail,
  onSelect,
  action
}: {
  page: RunPage
  pages: readonly RunPage[]
  review?: RunPageReview
  detail?: TabularIngestJobPage
  onSelect?: (pageId: string) => void
  action?: React.ReactNode
}) {
  const pagesInFile = pages.filter((candidate) => candidate.fileName === page.fileName).length
  const editing = review !== undefined && review.edits.draft?.pageId === page.pageId
  const reviewable =
    review !== undefined &&
    (page.status === 'needs_review' || review.editable || review.edited[page.pageId] !== undefined)
  const status = review && reviewable ? reviewStatus(page, review, editing) : ''
  const error = editing ? review?.edits.error : review?.error
  return (
    <div className="flex min-w-0 flex-col gap-1">
      <div className="flex flex-wrap items-center gap-2 text-xs">
        <span className="font-medium">
          {page.fileName} · page {page.pageNumber} of {pagesInFile}
        </span>
        <DecisionChip decision={review?.decisions[page.pageId]?.decision} status={page.status} />
        {page.confidence !== undefined ? (
          <span className="text-ink-3">page confidence {percent(page.confidence)}</span>
        ) : null}
        <div className="ml-auto flex flex-wrap items-center gap-1.5">
          {review && reviewable ? (
            <PageReviewActions
              page={page}
              pages={pages}
              review={review}
              detail={detail}
              onSelect={onSelect}
            />
          ) : null}
          {action && !editing ? <span className="ml-1">{action}</span> : null}
        </div>
      </div>
      {status ? <p className="text-11 text-ink-3">{status}</p> : null}
      {error ? (
        <p role="alert" className="text-11 text-st-down">
          {error}
        </p>
      ) : null}
    </div>
  )
}

/** Who decided the page and what its saved edits change, or how saving works while editing. */
function reviewStatus(page: RunPage, review: RunPageReview, editing: boolean): string {
  if (editing) {
    return `${review.edits.dirty ? 'Unsaved changes. ' : ''}Saved edits apply when the run commits. Types and required values are checked on save; keys and the config’s checks run at commit.`
  }
  const stamp = review.decisions[page.pageId]
  const excluded = stamp?.decision === 'excluded'
  const counts = review.edited[page.pageId]
  return [
    page.status !== 'needs_review'
      ? ''
      : stamp
        ? `${excluded ? 'Excluded' : 'Reviewed'} by ${stamp.by} ${formatRelativeTime(stamp.at)}.`
        : review.open
          ? ''
          : 'Not reviewed.',
    excluded && review.open ? 'None of this page’s rows will be committed.' : '',
    counts ? `Rows edited: ${tabularEditCountsText(counts)}.` : '',
    counts && review.open
      ? excluded
        ? 'Include the page again to edit its rows.'
        : 'The edits apply when the run commits.'
      : ''
  ]
    .filter(Boolean)
    .join(' ')
}

/**
 * Decide a flagged page, then move on to the next one still to review; edit
 * any page's rows. Nothing once the run has committed or been discarded.
 */
function PageReviewActions({
  page,
  pages,
  review,
  detail,
  onSelect
}: {
  page: RunPage
  pages: readonly RunPage[]
  review: RunPageReview
  detail?: TabularIngestJobPage
  onSelect?: (pageId: string) => void
}) {
  if (!review.open) return null
  const stamp = review.decisions[page.pageId]
  const flagged = page.status === 'needs_review'
  const next = nextAwaitingDecision(pages, page.pageId, review.decisions)
  const saving = review.saving !== undefined
  const { edits } = review
  const excluded = stamp?.decision === 'excluded'
  const decide = async (decision: RunReviewDecision | null) => {
    const saved = await review.decide(page.pageId, decision)
    if (saved && decision) onSelect?.(next?.pageId ?? page.pageId)
  }
  const startEditing = () => {
    if (!detail || !edits.confirmLeave()) return
    edits.start(savedPageDraft(page.pageId, `${page.fileName} page ${page.pageNumber}`, detail))
  }
  if (edits.draft?.pageId === page.pageId) {
    return (
      <div className="flex flex-wrap items-center gap-1.5" data-testid="page-review-bar">
        <Button
          variant="ghost"
          size="sm"
          className={cn(COMPACT_ACTION, edits.dirty && DESTRUCTIVE_ACTION)}
          disabled={edits.saving}
          onClick={edits.stop}
        >
          {edits.dirty ? 'Discard changes' : 'Done editing'}
        </Button>
        <WriteButton
          canManage={review.canManage}
          size="sm"
          className={COMPACT_ACTION}
          disabled={!edits.dirty || edits.saving}
          onClick={() => void edits.save()}
        >
          {edits.saving ? 'Saving…' : 'Save edits'}
        </WriteButton>
      </div>
    )
  }
  return (
    <div className="flex flex-wrap items-center gap-1.5" data-testid="page-review-bar">
      {review.editable ? (
        <WriteButton
          canManage={review.canManage}
          size="sm"
          variant="outline"
          className={COMPACT_ACTION}
          disabled={excluded || !detail || saving}
          title={excluded ? 'Include the page again to edit its rows' : undefined}
          onClick={startEditing}
        >
          Edit rows
        </WriteButton>
      ) : null}
      {!flagged ? null : stamp ? (
        <WriteButton
          canManage={review.canManage}
          size="sm"
          variant="ghost"
          className={COMPACT_ACTION}
          disabled={saving}
          onClick={() => void decide(null)}
        >
          {excluded ? 'Include again' : 'Reopen'}
        </WriteButton>
      ) : (
        <>
          <WriteButton
            canManage={review.canManage}
            size="sm"
            variant="outline"
            className={cn(COMPACT_ACTION, DESTRUCTIVE_ACTION, DESTRUCTIVE_OUTLINE)}
            disabled={saving}
            onClick={() => void decide('excluded')}
          >
            Exclude page
          </WriteButton>
          <WriteButton
            canManage={review.canManage}
            size="sm"
            className={COMPACT_ACTION}
            disabled={saving}
            onClick={() => void decide('reviewed')}
          >
            Mark page reviewed
          </WriteButton>
        </>
      )}
    </div>
  )
}

interface FlaggedRow {
  table: string
  row: number
  note?: string
  fields: Array<{ field: string; value: string; confidence: number }>
}

/** Each `needs_review` row the vision call read, with its fields below {@link LOW_CONFIDENCE}, lowest first. */
export function flaggedRows(reading: TabularRunPageReading): FlaggedRow[] {
  return reading.tables.flatMap((table) =>
    table.rows.flatMap((row, index) => {
      if (row.reviewStatus !== 'needs_review') return []
      const fields = Object.entries(row.fieldConfidence)
        .filter(([, confidence]) => confidence < LOW_CONFIDENCE)
        .sort(([, a], [, b]) => a - b)
        .map(([field, confidence]) => ({
          field,
          value: displayValue(row.values[field] ?? null),
          confidence
        }))
      return [{ table: table.table, row: index, note: row.reviewNote, fields }]
    })
  )
}

function PageReason({ page, reading }: { page: RunPage; reading?: TabularRunPageReading }) {
  const flagged = reading ? flaggedRows(reading) : []
  if (!page.reason && flagged.length === 0) return null
  const title =
    page.status === 'skipped'
      ? 'Why this page was skipped'
      : needsAttention(page)
        ? 'Why this page was flagged'
        : 'Extraction result'
  return (
    <section className="flex flex-col gap-1.5 text-xs" aria-label={title}>
      <h3 className="font-medium text-ink">{title}</h3>
      {page.reason ? <p className="text-ink-2">{page.reason}</p> : null}
      {flagged.map((row) => (
        <div
          key={`${row.table}-${row.row}`}
          className="rounded border-l-2 border-st-warn bg-st-warn-bg px-2.5 py-1.5"
          data-testid={`flag-${row.table}-${row.row}`}
        >
          <p className="text-11 text-ink">
            <span className="font-mono">{row.table}</span> row {row.row + 1}
          </p>
          {row.note ? <p className="text-11 text-ink-2">{row.note}</p> : null}
          {row.fields.length ? (
            <dl className="mt-1 grid grid-cols-[minmax(6rem,auto)_minmax(0,1fr)_auto] gap-x-2 gap-y-0.5 text-11">
              {row.fields.map((field) => (
                <React.Fragment key={field.field}>
                  <dt className="truncate font-mono text-ink-3">{field.field}</dt>
                  <dd className="break-words">{field.value}</dd>
                  <dd className="text-st-warn tabular-nums">{percent(field.confidence)} sure</dd>
                </React.Fragment>
              ))}
            </dl>
          ) : row.note ? null : (
            <p className="text-11 text-ink-3">The model flagged the row without naming a field.</p>
          )}
        </div>
      ))}
    </section>
  )
}

function ExtractionNotes({ page }: { page: RunPage }) {
  const hintFields = Object.entries(page.hints?.fields ?? {})
  const notes = [
    page.attempts && page.attempts > 1 ? `Read ${page.attempts} times.` : '',
    page.hint ? `Agent hint: ${page.hint}` : ''
  ].filter(Boolean)
  const preprocessNote =
    page.preprocess === 'failed'
      ? 'Preprocess failed; the vision call read the original page.'
      : page.preprocessError
        ? 'Preprocess warnings:'
        : ''
  if (!notes.length && !preprocessNote && !hintFields.length && !page.hints?.notes) return null
  return (
    <section className="flex flex-col gap-1 text-11 text-ink-2" aria-label="Extraction notes">
      {notes.map((note) => (
        <p key={note}>{note}</p>
      ))}
      {preprocessNote ? (
        <p>
          {preprocessNote}
          {page.preprocessError ? (
            <span className="mt-0.5 block font-mono text-ink-3">{page.preprocessError}</span>
          ) : null}
        </p>
      ) : null}
      {hintFields.length || page.hints?.notes ? (
        <div>
          <p className="font-medium text-ink">Preprocess hints</p>
          {hintFields.length ? (
            <dl className="mt-0.5 grid grid-cols-[auto_minmax(0,1fr)] gap-x-2 font-mono">
              {hintFields.map(([column, value]) => (
                <React.Fragment key={column}>
                  <dt className="text-ink-3">{column}</dt>
                  <dd className="break-words">{value}</dd>
                </React.Fragment>
              ))}
            </dl>
          ) : null}
          {page.hints?.notes ? <p className="mt-0.5">{page.hints.notes}</p> : null}
        </div>
      ) : null}
    </section>
  )
}

const UNAVAILABLE_TEXT: Record<NonNullable<TabularIngestJobPageTable['unavailable']>, string> = {
  no_page_column: 'has no source_page_urn column, so its rows can’t be matched to a page',
  not_stored: 'this run kept no stored rows to match',
  no_page_urn: 'this run does not record which rows came from each page',
  unreadable: 'the stored rows could not be read'
}

/** A table on the page being edited: its draft, the refusals of the last save, and how to change it. */
interface PageTableEdit {
  draft?: TableEditDraft
  issues: TabularPageEditIssue[]
  update: RunPageEdits['update']
  disabled: boolean
}

/**
 * A table's rows from this page. Service-filled columns and columns empty on
 * the page are hidden. When the table keeps the page table's columns and
 * row count, each cell shows the confidence the vision call read it with.
 * Saved review edits show on the rows; with `edit`, every column a reviewer
 * may change takes input.
 */
function PageTableRows({
  table,
  reading,
  highlight,
  decision,
  held,
  edit
}: {
  table: TabularIngestJobPageTable
  reading?: TabularRunPageReading['tables'][number]
  highlight?: number
  decision?: RunReviewDecision
  /** The run is still held, so a decision has not been applied yet. */
  held: boolean
  edit?: PageTableEdit
}) {
  const highlightRef = React.useRef<HTMLTableRowElement>(null)
  React.useEffect(() => {
    highlightRef.current?.scrollIntoView?.({ block: 'nearest' })
  }, [highlight, table.table])

  if (edit && !table.unavailable) {
    return <EditableTableRows table={table} reading={reading} edit={edit} />
  }
  const added = table.added ?? []
  const counts = {
    changed: table.rows.reduce((sum, row) => sum + Object.keys(row.changes ?? {}).length, 0),
    added: added.length,
    deleted: table.rows.filter((row) => row.deleted).length
  }
  const editsText = tabularEditCountsText(counts)
  const label = (
    <p className="text-xs">
      <span className="font-mono font-medium text-ink">{table.table}</span>
      <span className="text-ink-3">
        {' '}
        ·{' '}
        {table.unavailable
          ? UNAVAILABLE_TEXT[table.unavailable]
          : `${table.rows.length} ${table.rows.length === 1 ? 'row' : 'rows'} from this page`}
        {editsText ? ` · edited in review: ${editsText}` : ''}
      </span>
    </p>
  )
  const decisionNote = !decision
    ? ''
    : table.unavailable === 'no_page_column'
      ? `This page’s decision does not change ${table.table}: its rows ${held ? 'commit' : 'were committed'} as extracted.`
      : table.unavailable || table.rows.length === 0
        ? ''
        : decision === 'excluded'
          ? `Excluded: these rows ${held ? 'are left out of the commit' : 'were not committed'}.`
          : held
            ? 'Reviewed: these rows commit with needs_review = false.'
            : 'Reviewed: these rows were committed with needs_review = false.'
  if (table.unavailable || (table.rows.length === 0 && added.length === 0)) {
    return (
      <section aria-label={`${table.table} rows from this page`}>
        {label}
        {decisionNote ? <p className="mt-0.5 text-11 text-ink-3">{decisionNote}</p> : null}
      </section>
    )
  }

  const matched = matchedReadingRows(table, reading)
  const shown = table.columns
    .map((name, index) => ({ name, index }))
    .filter(
      ({ name, index }) =>
        !isTabularServiceFilledColumn(name) &&
        [...table.rows, ...added].some((row) => (row.cells[index] ?? '') !== '')
    )
  const serviceHidden = table.columns.filter(isTabularServiceFilledColumn)
  const emptyHidden = table.columns.filter(
    (name) => !isTabularServiceFilledColumn(name) && !shown.some((column) => column.name === name)
  )

  return (
    <section className="min-w-0" aria-label={`${table.table} rows from this page`}>
      {label}
      {decisionNote ? <p className="mt-0.5 text-11 text-ink-3">{decisionNote}</p> : null}
      <div
        className={cn(
          'mt-1.5 max-h-96 overflow-auto rounded border border-line',
          decision === 'excluded' && 'opacity-50'
        )}
      >
        <table className="min-w-full text-left font-mono text-11">
          <thead className="sticky top-0 bg-bg-sunk">
            <tr className="border-b border-line">
              <th
                className="px-2 py-1.5 text-right font-medium text-ink-3"
                title="Row in the table"
              >
                #
              </th>
              {shown.map(({ name }) => (
                <th key={name} className="px-2 py-1.5 font-medium whitespace-nowrap text-ink">
                  {name}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {table.rows.map((row, rowIndex) => {
              const readingRow = matched?.[rowIndex]
              const flagged = readingNeedsReview(readingRow)
              const highlighted = highlight === row.position
              return (
                <tr
                  key={row.position}
                  ref={highlighted ? highlightRef : undefined}
                  data-testid={`page-output-row-${table.table}-${row.position}`}
                  data-flagged={flagged || undefined}
                  data-highlighted={highlighted || undefined}
                  data-deleted={row.deleted ? true : undefined}
                  title={
                    row.deleted
                      ? `Deleted in review by ${row.deleted.by} ${formatRelativeTime(row.deleted.at)}`
                      : undefined
                  }
                  className={cn(
                    'border-b border-line last:border-0',
                    flagged && decision !== 'excluded' && !row.deleted && 'bg-st-warn-bg',
                    row.deleted && 'bg-st-down-bg/60 text-ink-3 line-through',
                    highlighted && 'outline-2 -outline-offset-2 outline-st-info'
                  )}
                >
                  <td className="px-2 py-1.5 text-right text-ink-3 tabular-nums">
                    {row.position + 1}
                  </td>
                  {shown.map(({ name, index }) => {
                    const change = row.changes?.[name]
                    const confidence = change ? undefined : readingRow?.fieldConfidence[name]
                    const low = confidence !== undefined && confidence < LOW_CONFIDENCE
                    const cell = row.cells[index] ?? ''
                    return (
                      <td
                        key={name}
                        className={cn('px-2 py-1.5 whitespace-nowrap', change && 'bg-st-info-bg')}
                        title={
                          change
                            ? `Changed in review by ${change.by} ${formatRelativeTime(change.at)}; the run read ${change.original === '' ? 'nothing' : change.original}`
                            : undefined
                        }
                      >
                        {cell === '' ? <span className="text-ink-3">—</span> : cell}
                        {change ? (
                          <s className="ml-1.5 text-10 text-ink-3">
                            {change.original === '' ? '—' : change.original}
                          </s>
                        ) : null}
                        {low ? (
                          <span
                            title={`The model read this value with ${percent(confidence)} confidence`}
                            className="ml-1.5 rounded-sm border border-st-warn/40 bg-background/70 px-1 py-px text-10 font-medium text-st-warn tabular-nums"
                          >
                            {percent(confidence)}
                          </span>
                        ) : null}
                      </td>
                    )
                  })}
                </tr>
              )
            })}
            {added.map((row) => (
              <tr
                key={row.id}
                data-testid={`page-added-row-${table.table}-${row.id}`}
                title={`Added in review by ${row.by} ${formatRelativeTime(row.at)}`}
                className="border-b border-line bg-st-info-bg last:border-0"
              >
                <td className="px-2 py-1.5 text-right text-10 text-st-info">new</td>
                {shown.map(({ name, index }) => {
                  const cell = row.cells[index] ?? ''
                  return (
                    <td key={name} className="px-2 py-1.5 whitespace-nowrap">
                      {cell === '' ? <span className="text-ink-3">—</span> : cell}
                    </td>
                  )
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {serviceHidden.length || emptyHidden.length ? (
        <p className="mt-1 text-11 text-ink-3">
          Hidden:{' '}
          {[
            serviceHidden.length ? `${serviceHidden.join(', ')} set by the run` : '',
            emptyHidden.length ? `${emptyHidden.join(', ')} empty on this page` : ''
          ]
            .filter(Boolean)
            .join('; ')}
          .
        </p>
      ) : null}
    </section>
  )
}

const NO_TABLE_EDITS: TableEditDraft = { changed: {}, deleted: {}, added: [] }

/** A table's rows from the page being edited: every column a reviewer may change is an input. */
function EditableTableRows({
  table,
  reading,
  edit
}: {
  table: TabularIngestJobPageTable
  reading?: TabularRunPageReading['tables'][number]
  edit: PageTableEdit
}) {
  const draft = edit.draft ?? NO_TABLE_EDITS
  const columns = table.columns
    .map((name, index) => ({ name, index }))
    .filter(({ name }) => !isTabularServiceFilledColumn(name))
  const serviceFilled = table.columns.filter(isTabularServiceFilledColumn)
  const update = edit.update
  const issueFor = (row: { position?: number; addedId?: string }, column?: string) =>
    edit.issues.find(
      (issue) =>
        (row.position !== undefined
          ? issue.position === row.position
          : issue.addedId === row.addedId) && issue.column === column
    )
  const typeText = (name: string) => table.columnTypes?.[name]?.type.toLowerCase() ?? ''
  const required = (name: string) => table.columnTypes?.[name]?.required === true
  const anyRequired = columns.some(({ name }) => required(name))
  const matched = matchedReadingRows(table, reading)
  const issueWhere = (issue: TabularPageEditIssue) => {
    const row =
      issue.position !== undefined
        ? `Row ${issue.position + 1}`
        : `New row ${draft.added.findIndex((added) => added.id === issue.addedId) + 1}`
    return issue.column ? `${row}, ${issue.column}` : row
  }
  return (
    <section className="min-w-0" aria-label={`${table.table} rows from this page`}>
      <p className="text-xs">
        <span className="font-mono font-medium text-ink">{table.table}</span>
        <span className="text-ink-3">
          {' '}
          · editing {table.rows.length} {table.rows.length === 1 ? 'row' : 'rows'} from this page
        </span>
      </p>
      <div className="mt-1.5 max-h-96 overflow-auto rounded border border-line">
        <table className="min-w-full text-left font-mono text-11">
          <thead className="sticky top-0 z-[1] bg-bg-sunk">
            <tr className="border-b border-line">
              <th
                className="px-2 py-1.5 text-right font-medium text-ink-3"
                title="Row in the table"
              >
                #
              </th>
              {columns.map(({ name }) => (
                <th key={name} className="px-2 py-1.5 font-medium whitespace-nowrap text-ink">
                  {name}
                  {required(name) ? (
                    <span className="text-st-down" title="Required">
                      *
                    </span>
                  ) : null}
                  {typeText(name) ? (
                    <span className="ml-1 text-10 font-normal text-ink-3">{typeText(name)}</span>
                  ) : null}
                </th>
              ))}
              <th className="px-2 py-1.5">
                <span className="sr-only">Row actions</span>
              </th>
            </tr>
          </thead>
          <tbody>
            {table.rows.map((row, rowIndex) => {
              const deleted = draft.deleted[row.position] === true
              const flagged = readingNeedsReview(matched?.[rowIndex])
              const rowIssue = issueFor({ position: row.position })
              return (
                <tr
                  key={row.position}
                  data-testid={`page-edit-row-${table.table}-${row.position}`}
                  data-deleted={deleted || undefined}
                  data-flagged={flagged || undefined}
                  title={rowIssue?.message}
                  className={cn(
                    'border-b border-line last:border-0',
                    deleted ? 'bg-st-down-bg/60' : flagged && 'bg-st-warn-bg',
                    rowIssue && 'outline-1 -outline-offset-1 outline-st-down'
                  )}
                >
                  <td className="px-2 py-1 text-right text-ink-3 tabular-nums">
                    {row.position + 1}
                  </td>
                  {columns.map(({ name, index }) => {
                    const original = row.changes?.[name]?.original ?? row.cells[index] ?? ''
                    return (
                      <td key={name} className="px-1 py-0.5">
                        <CellInput
                          label={`${name}, row ${row.position + 1}`}
                          value={draft.changed[row.position]?.[name] ?? original}
                          original={original}
                          placeholder={typeText(name)}
                          issue={issueFor({ position: row.position }, name)?.message}
                          disabled={deleted || edit.disabled}
                          struck={deleted}
                          onChange={(value) =>
                            update((current) =>
                              setStoredCell(current, table.table, table.columns, row, name, value)
                            )
                          }
                        />
                      </td>
                    )
                  })}
                  <td className="px-1 py-0.5 text-right">
                    <Button
                      variant="ghost"
                      size="sm"
                      className={cn(COMPACT_ACTION, !deleted && DESTRUCTIVE_ACTION)}
                      disabled={edit.disabled}
                      aria-label={`${deleted ? 'Restore' : 'Delete'} row ${row.position + 1}`}
                      onClick={() =>
                        update((current) => toggleDeletedRow(current, table.table, row.position))
                      }
                    >
                      {deleted ? 'Restore' : 'Delete'}
                    </Button>
                  </td>
                </tr>
              )
            })}
            {draft.added.map((row, addedIndex) => {
              const rowIssue = issueFor({ addedId: row.id })
              return (
                <tr
                  key={row.id}
                  data-testid={`page-edit-added-${table.table}-${row.id}`}
                  title={rowIssue?.message}
                  className={cn(
                    'border-b border-line bg-st-info-bg last:border-0',
                    rowIssue && 'outline-1 -outline-offset-1 outline-st-down'
                  )}
                >
                  <td className="px-2 py-1 text-right text-10 text-st-info">new</td>
                  {columns.map(({ name }) => (
                    <td key={name} className="px-1 py-0.5">
                      <CellInput
                        label={`${name}, new row ${addedIndex + 1}`}
                        value={row.values[name] ?? ''}
                        placeholder={typeText(name)}
                        issue={issueFor({ addedId: row.id }, name)?.message}
                        disabled={edit.disabled}
                        added
                        onChange={(value) =>
                          update((current) =>
                            setAddedCell(current, table.table, row.id, name, value)
                          )
                        }
                      />
                    </td>
                  ))}
                  <td className="px-1 py-0.5 text-right">
                    <Button
                      variant="ghost"
                      size="sm"
                      className={cn(COMPACT_ACTION, DESTRUCTIVE_ACTION)}
                      disabled={edit.disabled}
                      aria-label={`Remove new row ${addedIndex + 1}`}
                      onClick={() =>
                        update((current) => removeAddedRow(current, table.table, row.id))
                      }
                    >
                      Remove
                    </Button>
                  </td>
                </tr>
              )
            })}
          </tbody>
        </table>
      </div>
      <div className="mt-1 flex flex-wrap items-center gap-2">
        <Button
          variant="ghost"
          size="sm"
          className={cn(
            COMPACT_ACTION,
            'border border-dashed border-st-info/50 bg-st-info-bg text-st-info hover:bg-st-info-bg/60 hover:text-st-info'
          )}
          disabled={edit.disabled}
          onClick={() => update((current) => addDraftRow(current, table.table))}
        >
          + Add row
        </Button>
        {anyRequired || serviceFilled.length ? (
          <span className="text-11 text-ink-3">
            {anyRequired ? (
              <>
                <span className="text-st-down">*</span> Required.{' '}
              </>
            ) : null}
            {serviceFilled.length
              ? `${serviceFilled.join(', ')} ${serviceFilled.length === 1 ? 'is' : 'are'} set by the run.`
              : ''}
          </span>
        ) : null}
      </div>
      {edit.issues.length ? (
        <ul role="alert" className="mt-1 flex flex-col gap-0.5 text-11 text-st-down">
          {edit.issues.map((issue, index) => (
            <li key={index}>
              {issueWhere(issue)}: {issue.message}
            </li>
          ))}
        </ul>
      ) : null}
    </section>
  )
}

function CellInput({
  label,
  value,
  original,
  placeholder,
  issue,
  disabled,
  struck,
  added,
  onChange
}: {
  label: string
  value: string
  /** What the run read, for a stored row's cell. */
  original?: string
  placeholder: string
  issue?: string
  disabled: boolean
  struck?: boolean
  /** The cell of a row the reviewer added. */
  added?: boolean
  onChange: (value: string) => void
}) {
  const changed = original !== undefined && value !== original
  return (
    <input
      aria-label={label}
      aria-invalid={issue ? true : undefined}
      title={
        issue ?? (changed ? `The run read ${original === '' ? 'nothing' : original}` : undefined)
      }
      className={cn(
        'w-full min-w-[5rem] rounded border bg-transparent px-1 py-0.5 font-mono text-11 outline-none focus:border-st-info disabled:cursor-not-allowed',
        issue
          ? 'border-st-down bg-st-down-bg'
          : changed
            ? 'border-st-info bg-st-info-bg'
            : added
              ? 'border-st-info/50'
              : 'border-line',
        struck && 'text-ink-3 line-through'
      )}
      value={value}
      placeholder={placeholder}
      disabled={disabled}
      onChange={(event) => onChange(event.target.value)}
    />
  )
}

/**
 * What the vision call read for each of the table's rows, when the table kept
 * the page table's rows one for one. The rows' `needs_review` cell carries the
 * page's flag, so only the reading says which rows the model was unsure of.
 */
function matchedReadingRows(
  table: TabularIngestJobPageTable,
  reading: TabularRunPageReading['tables'][number] | undefined
): TabularRunPageReading['tables'][number]['rows'] | undefined {
  return reading && reading.rows.length === table.rows.length ? reading.rows : undefined
}

/** The model flagged the row, or read one of its fields with low confidence. */
function readingNeedsReview(
  row: TabularRunPageReading['tables'][number]['rows'][number] | undefined
): boolean {
  if (!row) return false
  return (
    row.reviewStatus === 'needs_review' ||
    Object.values(row.fieldConfidence).some((confidence) => confidence < LOW_CONFIDENCE)
  )
}

function displayValue(value: string | number | boolean | null): string {
  return value === null || value === '' ? '—' : String(value)
}
